import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Stack,
  Alert,
  LoadingOverlay,
  SimpleGrid,
  Group,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft, IconEdit, IconEye, IconDownload, IconPrinter } from '@tabler/icons-react';
import { useMedplumProfile } from '@medplum/react';
import { Practitioner } from '@medplum/fhirtypes';
import {
  DiagnosticReportHeader,
  ReviewStatusCard,
  CommunicationsCard,
  ReviewCommentsCard,
} from './components';
import { useDiagnosticReport } from './hooks/useDiagnosticReport';
import { useReviewStatus } from './hooks/useReviewStatus';
import { useCommunications } from './hooks/useCommunications';
import { useCommentSubmission } from './hooks/useCommentSubmission';
import { usePdfActions } from './hooks/usePdfActions';
import { ReportEditor, ReportEditorRef } from '../report-editor/ReportEditor';
import { useReportEditor } from '../report-editor/hooks/useReportEditor';
import { ROUTES } from '@/consts/routes.consts';
import { useTranslation } from 'react-i18next';

// -----------------------------------------------------------------------------
export const DiagnosticReportDetail = () => {
  const navigate = useNavigate();
  const { orderId, reportId } = useParams<{
    orderId: string;
    reportId: string;
  }>();
  const { t } = useTranslation(['lims', 'common']);

  // ---------------------------------------------------------------------------
  const currentUser = useMedplumProfile() as Practitioner;

  const editorRef = useRef<ReportEditorRef>(null);

  const {
    loading,
    diagnosticReport,
    setDiagnosticReport,
  } = useDiagnosticReport(reportId, orderId);

  // Report editor state
  const {
    loading: editorLoading,
    error: _editorError,
    template,
    bindingData,
    savedContent,
    isReady,
  } = useReportEditor({
    orderId,
    templateId: 'neurocutaneous-syndromes',
    reportId,
  });

  const {
    reviewStatusData,
    currentUserReviewStatus,
    setCurrentUserReviewStatus,
    refetch: refetchReviewStatus,
  } = useReviewStatus(reportId, currentUser);

  const { communications, communicationsLoading, loadCommunications } =
    useCommunications(reportId);

  const {
    submittingComment,
    reviewStatus,
    setReviewStatus,
    reviewComment,
    setReviewComment,
    submitComment,
  } = useCommentSubmission({
    reportId,
    diagnosticReport,
    currentUser,
    onCommentSubmitted: loadCommunications,
    onReviewStatusChange: setCurrentUserReviewStatus,
    onDiagnosticReportUpdate: setDiagnosticReport,
  });

  const { pdfLoading, handlePreviewPDF, handleExportPDF, handlePrint } = usePdfActions({
    editorRef,
    template,
    content: savedContent,
    orderId,
  });

  // ---------------------------------------------------------------------------
  // Auto-set review status to comment if user has already approved
  useEffect(() => {
    if (currentUserReviewStatus === 'approved') {
      setReviewStatus('comment');
    }
  }, [currentUserReviewStatus, setReviewStatus]);

  // ---------------------------------------------------------------------------
  // Load communications on mount
  useEffect(() => {
    loadCommunications();
  }, [loadCommunications]);

  // ---------------------------------------------------------------------------
  // Helper functions for conclusion display
  const getConclusionDisplay = () => {
    const conclusionCode = diagnosticReport?.conclusionCode?.[0];
    if (conclusionCode?.text) {
      return conclusionCode.text;
    }

    return conclusionCode?.coding?.[0]?.display || 'Unknown';
  };

  // ---------------------------------------------------------------------------
  const getConclusionColor = () => {
    const conclusion = getConclusionDisplay().toLowerCase();
    if (conclusion.includes('positive')) {
      return 'red';
    }

    if (conclusion.includes('negative')) {
      return 'green';
    }

    return 'blue';
  };

  // ---------------------------------------------------------------------------
  const handleNavigateBack = () => {
    navigate(`/lims/order/${orderId}`);
  };

  // ---------------------------------------------------------------------------
  const handleSubmitComment = async () => {
    await submitComment();
    await refetchReviewStatus();
  };

  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="relative h-96">
        <LoadingOverlay visible />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  if (!diagnosticReport) {
    return (
      <Stack gap="md" className="p-6">
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Diagnostic report not found
        </Alert>
        <Button
          leftSection={<IconArrowLeft size={16} />}
          onClick={handleNavigateBack}
        >
          Back to Order
        </Button>
      </Stack>
    );
  }
  // ---------------------------------------------------------------------------
  return (
    <Stack gap="md" className="p-6">
      {/* Header */}
      <DiagnosticReportHeader
        diagnosticReport={diagnosticReport}
        orderId={orderId}
        onNavigateBack={handleNavigateBack}
        getConclusionDisplay={getConclusionDisplay}
        getConclusionColor={getConclusionColor}
      />

      {/* Report Actions */}
      <Group justify="space-between" align="center">
      <Group gap="sm">
        <Button
          variant="light"
          leftSection={<IconEye size={16} />}
          onClick={handlePreviewPDF}
        >
          {t('reportEditor.pdf.preview', { ns: 'lims' })}
        </Button>

        <Button
          variant="filled"
          leftSection={<IconDownload size={16} />}
          onClick={handleExportPDF}
          loading={pdfLoading}
        >
          {t('reportEditor.pdf.export', { ns: 'lims' })}
        </Button>

        <Button
          variant="outline"
          leftSection={<IconPrinter size={16} />}
          onClick={handlePrint}
          loading={pdfLoading}
        >
          {t('reportEditor.pdf.print', { ns: 'lims' })}
        </Button>
      </Group>

        {diagnosticReport.status === 'preliminary' && (
          <Button
            variant="light"
            leftSection={<IconEdit size={16} />}
            onClick={() =>
              navigate(ROUTES.LIMS.EDIT_REPORT_EXISTING(orderId || '', reportId || ''))
            }
          >
            Edit Report
          </Button>
        )}
      </Group>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="xl">
        {/* Left Column - Report Content */}
        {template && isReady ? (
            <ReportEditor
              ref={editorRef}
              template={template}
              initialContent={savedContent}
              bindingData={bindingData}
              loading={editorLoading}
              readOnly
            />
          ) : (
            <LoadingOverlay visible />
          )
        }

        {/* Right Column - Communications and Review */}
        <Stack gap="md">
          <ReviewStatusCard
            diagnosticReport={diagnosticReport}
            reviewStatusData={reviewStatusData}
          />

          <CommunicationsCard
            communications={communications}
            communicationsLoading={communicationsLoading}
          />

          <ReviewCommentsCard
            diagnosticReport={diagnosticReport}
            reviewStatusData={reviewStatusData}
            currentUserReviewStatus={currentUserReviewStatus}
            reviewStatus={reviewStatus}
            setReviewStatus={setReviewStatus}
            reviewComment={reviewComment}
            setReviewComment={setReviewComment}
            submittingComment={submittingComment}
            onSubmitComment={handleSubmitComment}
          />
        </Stack>
      </SimpleGrid>
    </Stack>
  );
};
